/**
 * unitRateCalc.js — Unit Rate calculation engine.
 *
 * Pure functions that mirror the "LGL Fabrication Combined Hourly Rate
 * Template" workbook formulas. Kept dependency-free so it can be unit
 * tested (UnitRateCalc.test.js) and reused outside the page component.
 */

export const SUMMARY_GROUPS = [
  { key: 'direct', label: 'Direct', defaultMu: 0.26, color: '#0ea5e9' },
  { key: 'other_direct', label: 'Other Direct', defaultMu: 0.26, color: '#6366f1' },
  { key: 'support_staff', label: 'Support Staff', defaultMu: 0.26, color: '#8b5cf6' },
  { key: 'supervisory', label: 'Supervisory', defaultMu: 0.26, color: '#ec4899' },
  { key: 'materials', label: 'Materials', defaultMu: 0.10, color: '#f59e0b' },
  { key: 'subs', label: 'Subs', defaultMu: 0.10, color: '#10b981' },
  { key: 'rentals', label: 'Rentals', defaultMu: 0.10, color: '#14b8a6' },
  { key: 'room_board', label: 'Room and Board', defaultMu: 0.10, color: '#64748b' },
];
export const DEFAULT_MARKUPS = Object.fromEntries(SUMMARY_GROUPS.map((g) => [g.key, g.defaultMu]));

const n = (v) => {
  if (v === undefined || v === null || v === '') return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};
export const nz = (v) => n(v) ?? 0;

export const stHours = (shift, days) => Math.min(nz(shift) * nz(days), 40);
export const otHours = (shift, days) => Math.max(nz(shift) * (nz(days) - 4), 0);
export const blendedRate = (base, ot, st, otH) => {
  const denom = st + otH;
  if (!denom) return 0;
  return (nz(base) * st + nz(ot) * otH) / denom;
};

let uidCounter = 1;
export const uid = () => `u${uidCounter++}`;

/* Default template = the "Hatch PP133" workbook that this module replicates */
export const defaultSheet = () => ({
  id: null,
  name: 'New Combined Hourly Rate',
  client: '',
  reference: '',
  notes: '',
  crew_shift_hours: 10, crew_shift_days: 4,
  supv_shift_hours: 11, supv_shift_days: 4,
  total_direct_labour_hours: 2813.7,
  crew_ratio: [
    { occupation: 'PF', ratio: 4 },
    { occupation: 'MT', ratio: 2 },
    { occupation: 'Apprentice', ratio: '' },
  ],
  xray_weld_count: 710, xray_requirement_pct: 0.08, xray_cost_each: 250,
  xray_total_cost: 26200, xray_total_hours: 2451.58,
  markups: { ...DEFAULT_MARKUPS },
  payment_terms_pct: 0,
  lines: [
    { _id: uid(), section: 'direct', group_name: 'Direct Labour', description: 'Pipefitter/Welder', base_rate: 85.54, ot_rate: 132.59, pct_labour: 0.666 },
    { _id: uid(), section: 'direct', group_name: 'Direct Labour', description: 'Metal Trade', base_rate: 61.06, ot_rate: 94.66, pct_labour: 0.333 },
    { _id: uid(), section: 'direct', group_name: 'Direct Labour', description: 'Apprentice', base_rate: 54.03, ot_rate: 127.8, pct_labour: 0 },
    { _id: uid(), section: 'other_direct', group_name: 'Other Direct', description: 'Material Handling', pct_labour: 0.08 },
    { _id: uid(), section: 'other_direct', group_name: 'Other Direct', description: 'Job Setup', pct_labour: 0, notes: 'Required if over 30 trades people' },
    { _id: uid(), section: 'other_direct', group_name: 'Other Direct', description: 'Welder Qualification', pct_labour: 0.01 },
    { _id: uid(), section: 'other_direct', group_name: 'Other Direct', description: 'Safety Orientation', pct_labour: 0, notes: 'Required if over 30 trades people' },
    { _id: uid(), section: 'other_direct', group_name: 'Other Direct', description: 'Rework', pct_labour: 0.02 },
    { _id: uid(), section: 'other_direct', group_name: 'Other Direct', description: 'Fitting to fitting', pct_labour: 0 },
    { _id: uid(), section: 'staff', group_name: 'Supervisory', description: 'Foreman', base_rate: 94.34, ot_rate: 171.25, pct_labour: 0.3, other_direct_pct: 0.06 },
    { _id: uid(), section: 'staff', group_name: 'Supervisory', description: 'General Foreman', base_rate: 96.35, ot_rate: 174.9, pct_labour: 0.05, other_direct_pct: 0.06 },
    { _id: uid(), section: 'staff', group_name: 'Supervisory', description: 'Superintendent', base_rate: 101.17, ot_rate: 183.65, pct_labour: 0, other_direct_pct: 0.06 },
    { _id: uid(), section: 'staff', group_name: 'Support Staff', description: 'Scheduler', base_rate: '', ot_rate: '', pct_labour: '', other_direct_pct: 0.06 },
    { _id: uid(), section: 'staff', group_name: 'Support Staff', description: 'Quality Control', base_rate: 66.68, ot_rate: 124.3, pct_labour: 0.15, other_direct_pct: 0.06 },
    { _id: uid(), section: 'staff', group_name: 'Support Staff', description: 'Cost Clerk', base_rate: 67.94, ot_rate: 124.3, pct_labour: 0, other_direct_pct: 0.06 },
    { _id: uid(), section: 'staff', group_name: 'Support Staff', description: 'Project Manager', base_rate: 114.67, ot_rate: 166.3, pct_labour: 0.1, other_direct_pct: 0.06 },
    { _id: uid(), section: 'staff', group_name: 'Support Staff', description: 'Technical Coordinator', base_rate: 83.1, ot_rate: 119.89, pct_labour: 0.065, other_direct_pct: 0.06 },
    { _id: uid(), section: 'staff', group_name: 'Support Staff', description: 'Safety Officer', base_rate: 64.16, ot_rate: 92.05, pct_labour: 0, other_direct_pct: 0.06 },
    { _id: uid(), section: 'staff', group_name: 'Support Staff', description: 'Document Control', base_rate: 50.26, ot_rate: 71.62, pct_labour: 0.02, other_direct_pct: 0.06 },
    { _id: uid(), section: 'msr', group_name: 'Materials', description: 'Consumables', pct_labour: 0.08, link_type: 'none' },
    { _id: uid(), section: 'msr', group_name: 'Subs', description: 'X-ray', link_type: 'xray' },
    { _id: uid(), section: 'msr', group_name: 'Subs', description: 'Transportation', blended_rate: 0, link_type: 'none' },
    { _id: uid(), section: 'msr', group_name: 'Rentals', description: 'Outside Rental', blended_rate: 0, link_type: 'none', notes: 'Required if over 30 trades people' },
    { _id: uid(), section: 'msr', group_name: 'Rentals', description: 'LGL forklift', link_type: 'equipment', linked_description: 'Forklift' },
    { _id: uid(), section: 'msr', group_name: 'Rentals', description: 'LGL shunt truck', link_type: 'equipment', linked_description: 'Shunt Truck' },
    { _id: uid(), section: 'msr', group_name: 'Rentals', description: 'Vehicles', link_type: 'equipment', linked_description: 'Pickup' },
    { _id: uid(), section: 'room_board', group_name: 'Room and Board', description: 'Direct Craft R&B', rate_per_hour: 11, pct_labour: 0, notes: 'Required if over 30 trades people' },
    { _id: uid(), section: 'room_board', group_name: 'Room and Board', description: 'Direct Craft Travel', rate_per_hour: 7.5, pct_labour: 0 },
    { _id: uid(), section: 'equipment', group_name: 'Equipment', description: 'Shunt Truck', weekly_rate: 1000, pct_use: 0, week_hours: 40 },
    { _id: uid(), section: 'equipment', group_name: 'Equipment', description: 'Forklift', weekly_rate: 600, pct_use: 0, week_hours: 40 },
    { _id: uid(), section: 'equipment', group_name: 'Equipment', description: 'Pickup', weekly_rate: 300, pct_use: 0, week_hours: 40 },
  ],
});

/**
 * Recompute every derived value on a sheet (mirrors the workbook):
 *   ST = min(shift x days, 40), OT = max(shift x (days-4), 0)
 *   Blended = (base x ST + OT rate x OT) / (ST + OT)
 *   Direct rc        = % labour x blended
 *   Other direct rc  = % labour x direct total
 *   Staff rc         = blended x (% labour x (1 + other direct %))
 *   Materials rc     = % labour x (direct + other direct totals)
 *   Subs/Rentals rc  = own rate (manual, X-ray calc, or equipment link)
 *   Room & board rc  = $/hour x % labour
 *   Selling          = per-group cost x (1 + markup); final x (1 + payment terms)
 */
export function computeSheet(sheet) {
  const crewST = stHours(sheet.crew_shift_hours, sheet.crew_shift_days);
  const crewOT = otHours(sheet.crew_shift_hours, sheet.crew_shift_days);
  const supvST = stHours(sheet.supv_shift_hours, sheet.supv_shift_days);
  const supvOT = otHours(sheet.supv_shift_hours, sheet.supv_shift_days);

  const lines = sheet.lines || [];
  const by = (s) => lines.filter((l) => l.section === s);

  // Equipment costs (Cost = weekly x % use / working week hours)
  const equipment = by('equipment').map((l) => {
    const hrs = nz(l.week_hours) || (crewST + crewOT) || 40;
    return { ...l, cost: hrs ? (nz(l.weekly_rate) * nz(l.pct_use)) / hrs : 0 };
  });
  const equipCost = (desc) => equipment.find((e) => (e.description || '').toLowerCase() === (desc || '').toLowerCase())?.cost ?? 0;

  // X-ray side calc: cost per hour = total cost / total hours
  const xrayCostPerHour = nz(sheet.xray_total_hours) ? nz(sheet.xray_total_cost) / nz(sheet.xray_total_hours) : 0;

  const direct = by('direct').map((l) => {
    const blended = blendedRate(l.base_rate, l.ot_rate, crewST, crewOT);
    return { ...l, st: crewST, ot: crewOT, blended, rc: nz(l.pct_labour) * blended };
  });
  const directTotal = direct.reduce((s, l) => s + l.rc, 0);

  const otherDirect = by('other_direct').map((l) => ({ ...l, rc: nz(l.pct_labour) * directTotal }));
  const otherDirectTotal = otherDirect.reduce((s, l) => s + l.rc, 0);

  const staff = by('staff').map((l) => {
    const blended = blendedRate(l.base_rate, l.ot_rate, supvST, supvOT);
    return {
      ...l, st: supvST, ot: supvOT, blended,
      rc: blended * (nz(l.pct_labour) * (1 + nz(l.other_direct_pct))),
      equivHours: nz(sheet.total_direct_labour_hours) * nz(l.pct_labour),
    };
  });
  const staffTotal = staff.reduce((s, l) => s + l.rc, 0);
  const staffEquivHours = staff.reduce((s, l) => s + l.equivHours, 0);
  const supervisoryTotal = staff.filter((l) => l.group_name === 'Supervisory').reduce((s, l) => s + l.rc, 0);
  const supportStaffTotal = staff.filter((l) => l.group_name === 'Support Staff').reduce((s, l) => s + l.rc, 0);

  const msr = by('msr').map((l) => {
    let rate = nz(l.blended_rate);
    if (l.link_type === 'xray') rate = xrayCostPerHour;
    else if (l.link_type === 'equipment') rate = equipCost(l.linked_description);
    const rc = l.group_name === 'Materials'
      ? nz(l.pct_labour) * (directTotal + otherDirectTotal)
      : rate;
    return { ...l, rate, rc };
  });
  const msrTotal = msr.reduce((s, l) => s + l.rc, 0);
  const msrGroupTotal = (g) => msr.filter((l) => l.group_name === g).reduce((s, l) => s + l.rc, 0);

  const roomBoard = by('room_board').map((l) => ({ ...l, rc: nz(l.rate_per_hour) * nz(l.pct_labour) }));
  const roomBoardTotal = roomBoard.reduce((s, l) => s + l.rc, 0);

  const groupCosts = {
    direct: directTotal,
    other_direct: otherDirectTotal,
    support_staff: supportStaffTotal,
    supervisory: supervisoryTotal,
    materials: msrGroupTotal('Materials'),
    subs: msrGroupTotal('Subs'),
    rentals: msrGroupTotal('Rentals'),
    room_board: roomBoardTotal,
  };
  const markups = { ...DEFAULT_MARKUPS, ...(sheet.markups || {}) };
  const summary = SUMMARY_GROUPS.map((g) => {
    const cost = groupCosts[g.key] || 0;
    const mu = nz(markups[g.key]);
    return { ...g, cost, mu, selling: cost * (1 + mu) };
  });
  const totalCost = summary.reduce((s, g) => s + g.cost, 0);
  const totalSelling = summary.reduce((s, g) => s + g.selling, 0);
  const finalRate = totalSelling * (1 + nz(sheet.payment_terms_pct));

  return {
    crewST, crewOT, supvST, supvOT, xrayCostPerHour,
    direct, directTotal, otherDirect, otherDirectTotal,
    staff, staffTotal, staffEquivHours, supervisoryTotal, supportStaffTotal,
    msr, msrTotal, roomBoard, roomBoardTotal, equipment,
    summary, totalCost, totalSelling, finalRate, markups,
  };
}
